/**
 * Owner: Masa Bayu
 * File: qx-draft-image-place.ts
 * Date:Time: 2026-10-16 00:55
 * Description: Figure drag place · vertical reorder · horizontal float side.
 */

export type DraftPlaceFloat = 'left' | 'right' | 'none';

function blockChildren(editor: HTMLElement): HTMLElement[] {
  return Array.from(editor.children).filter((el): el is HTMLElement => el instanceof HTMLElement);
}

function dropTargetBefore(
  editor: HTMLElement,
  clientY: number,
  moving: HTMLElement,
): { ref: HTMLElement | null; place: 'before' | 'after' | 'end' } {
  const blocks = blockChildren(editor).filter((el) => el !== moving);
  if (blocks.length === 0) return { ref: null, place: 'end' };
  for (const block of blocks) {
    const rect = block.getBoundingClientRect();
    const mid = rect.top + rect.height / 2;
    if (clientY < mid) return { ref: block, place: 'before' };
  }
  return { ref: blocks[blocks.length - 1]!, place: 'after' };
}

/** Left / center / right third of the editor → float left / none / right. */
export function floatSideFromClientX(
  editor: HTMLElement,
  clientX: number,
): DraftPlaceFloat {
  const rect = editor.getBoundingClientRect();
  const width = Math.max(1, rect.width);
  const t = (clientX - rect.left) / width;
  if (t < 1 / 3) return 'left';
  if (t > 2 / 3) return 'right';
  return 'none';
}

/**
 * Reorder by Y · set wrap side by X (drag left/right moves image to that side).
 */
export function placeDraftFigure(
  editor: HTMLElement,
  figure: HTMLElement,
  clientX: number,
  clientY: number,
  setFloat: (figure: HTMLElement, side: DraftPlaceFloat) => void,
): void {
  const { ref, place } = dropTargetBefore(editor, clientY, figure);
  if (!ref || place === 'end') {
    editor.appendChild(figure);
  } else if (place === 'before') {
    if (ref !== figure) editor.insertBefore(figure, ref);
  } else if (ref.nextSibling !== figure) {
    editor.insertBefore(figure, ref.nextSibling);
  }
  setFloat(figure, floatSideFromClientX(editor, clientX));
}

export function autoScrollDraftCanvas(root: HTMLElement, clientY: number): void {
  const canvas = root.closest('.qxw-canvas');
  if (!(canvas instanceof HTMLElement)) return;
  const rect = canvas.getBoundingClientRect();
  const edge = 48;
  if (clientY < rect.top + edge) canvas.scrollTop -= 18;
  else if (clientY > rect.bottom - edge) canvas.scrollTop += 18;
}
